import { useCallback, useEffect, useState } from "react";

export interface MapExpansionControls {
  isExpanded: boolean;
  toggle: () => void;
  collapse: () => void;
  expand: () => void;
  setExpanded: (next: boolean) => void;
}

/**
 * Shared helper for map views that allows expanding to fullscreen, handles
 * Escape-to-close, and prevents the body from scrolling while expanded.
 */
export function useMapExpansion(initial = false): MapExpansionControls {
  const [isExpanded, setIsExpanded] = useState(initial);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsExpanded(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded]);

  const toggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const collapse = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const expand = useCallback(() => {
    setIsExpanded(true);
  }, []);

  return {
    isExpanded,
    toggle,
    collapse,
    expand,
    setExpanded: setIsExpanded,
  };
}
